/**
 * Logout bridge: registers for AUTH.LOGOUT_PROCEED and performs logout (session close + clear storage).
 * Replaces AuthService/LogoutService for app-level logout on beforeunload and when user clicks logout.
 */

import { useEffect, useRef } from 'react';
import Emitter from 'shared/events/emitter';
import EVENT_NAMES from 'shared/constants/events';
import RequestClient, { clearActiveVirtualSessionId, getActiveVirtualSessionId } from 'shared/api/http-client';
import ENDPOINTS from 'shared/api/endpoints';
import UrlModifier from 'shared/utils/url';
import { LocalStorage } from 'shared/utils/storage';
import { getRoutes } from 'shared/utils/runtimeBasePath';

function redirectToHome() {
  window.location.href = getRoutes().HOME;
}

function clearClientSessionState() {
  clearActiveVirtualSessionId();
  LocalStorage.remove('tokens');
  LocalStorage.remove('tokens_fetch_timestamp');
}

function onLogoutProceed() {
  const session_id = getActiveVirtualSessionId();
  const isGuest = !session_id;

  if (isGuest) {
    clearClientSessionState();
    if (typeof window !== 'undefined' && window.parent !== window) {
      window.parent.postMessage({ type: EVENT_NAMES.EMBEDDING.VIRTUAL_WEB_LOGOUT }, '*');
    }
    redirectToHome();
    return;
  }

  if (!session_id) {
    // Logged-in user but no active virtual session – clear and redirect
    clearClientSessionState();
    if (typeof window !== 'undefined' && window.parent !== window) {
      window.parent.postMessage({ type: EVENT_NAMES.EMBEDDING.VIRTUAL_WEB_LOGOUT }, '*');
    }
    redirectToHome();
    return;
  }

  Emitter.emit(EVENT_NAMES.ALERT.LOADING_DISPLAY, {
    title: 'Logging out',
    message: '',
    options: { showCloseButton: false, showCancelButton: false, allowOutsideClick: false },
  });

  const url = UrlModifier.bindToUrl(ENDPOINTS.SESSIONS_READ, {
    id: session_id ?? '',
  });
  const payload = { action: 'close' };

  RequestClient.post(url as string, payload)
    .then(() => {
      clearClientSessionState();
      if (typeof window !== 'undefined' && window.parent !== window) {
        window.parent.postMessage({ type: EVENT_NAMES.EMBEDDING.VIRTUAL_WEB_LOGOUT }, '*');
      }
      Emitter.emit(EVENT_NAMES.ALERT.SUCCESS_DISPLAY, {
        title: 'Logout success',
        message: '',
        options: { timer: 2000, onResolve: redirectToHome },
      });
      Emitter.emit(EVENT_NAMES.AUTH.LOGOUT_SUCCESS_AFTER, {});
      // Ensure redirect even if alert does not fire onResolve
      setTimeout(redirectToHome, 2500);
    })
    .catch(() => {
      clearClientSessionState();
      if (typeof window !== 'undefined' && window.parent !== window) {
        window.parent.postMessage({ type: EVENT_NAMES.EMBEDDING.VIRTUAL_WEB_LOGOUT }, '*');
      }
      Emitter.emit(EVENT_NAMES.AUTH.LOGOUT_SUCCESS_AFTER, {});
      redirectToHome();
    })
    .finally(() => {
      Emitter.emit(EVENT_NAMES.ALERT.LOADING_STOP);
    });
}

export function useLogoutBridge() {
  const handlerRef = useRef(() => onLogoutProceed());

  useEffect(() => {
    const handler = handlerRef.current;
    Emitter.register(EVENT_NAMES.AUTH.LOGOUT_PROCEED, handler);
    return () => {
      Emitter.off(EVENT_NAMES.AUTH.LOGOUT_PROCEED, handler);
    };
  }, []);
}

export function triggerLogoutOnBeforeUnload() {
  if (getActiveVirtualSessionId()) {
    onLogoutProceed();
  }
}
